// frontend/src/widgets/WorkflowBindingAdminWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-15
// Updated:      2026-10-06 18:06 BST
//
// Description:
//   Business-first workflow binding administration widget.
//
// Change Log (GMT/BST):
//   2026-10-06 18:06 BST - Made selected workflow/version context permanently visible in the binding heading
//                           and form. Binding remains version-safe: draft targets can be prepared, then created
//                           immediately after publish. Neutral widget styling retained throughout.
//   2026-10-06 17:54 BST - Restored selected workflow context in the binding widget. Draft versions
//                           now remain visibly associated with the binding form and taxonomy can be
//                           prepared before publish; binding creation remains correctly restricted
//                           to published versions by the backend contract. Normalised controls to
//                           the standard white/dark-text Workflow Admin styling.
//   2026-10-06 16:27 BST - Phase 1C.3 UX correction. Administrators now choose only
//                           Job vs Work Package and the applicable taxonomy hierarchy.
//                           Internal binding_scope is derived from the deepest selected
//                           taxonomy node. Binding identity is immutable after creation;
//                           target changes require deactivation and creation of a new binding.
//   2026-10-06 10:28 BST - Added dual-domain six-scope binding support.
//   2026-09-10          - Mobile responsiveness pass.
//
// Operational Notes:
//   - No API calls exist here; WorkflowPage owns mutations.
//   - A binding may only be created against a published workflow version.

import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";

const sameId = (a, b) => String(a ?? "") === String(b ?? "");
const childrenFor = (node, level) => {
  if (!node) return [];
  if (level === "subtype") return node.subtypes || node.children || [];
  return node.subtypes2 || node.subtype2s || node.children || [];
};
const findNode = (nodes, id) => (Array.isArray(nodes) ? nodes : []).find((n) => sameId(n?.id, id)) || null;
const toInputDate = (v) => { if (!v) return ""; const d = new Date(v); if (Number.isNaN(d.getTime())) return ""; const p=(n)=>String(n).padStart(2,"0"); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
const fromInputDate = (v) => { if (!v) return null; const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d.toISOString(); };

const emptyForm = { domain: "job", type_id: "", subtype_id: "", subtype2_id: "", priority: 100, is_default: false, is_active: true, effective_from: "", effective_to: "" };

const scopeMeta = {
  job_type: ["job", "taxonomy_type_id"], job_subtype: ["job", "taxonomy_subtype_id"], job_subtype2: ["job", "taxonomy_subtype2_id"],
  work_package_type: ["work_package", "work_package_type_id"], work_package_subtype: ["work_package", "work_package_subtype_id"], work_package_subtype2: ["work_package", "work_package_subtype2_id"],
};

const WorkflowBindingAdminWidget = ({
  selectedTemplate = null, selectedVersion = null, bindings = [], selectedBindingId = null,
  taxonomyTypes = [], workPackageTaxonomyTypes = [], loading = {},
  onSelectBinding, onCreateBinding, onUpdateBinding, onDeactivateBinding,
}) => {
  const { theme } = useTheme();
  const [form, setForm] = useState(emptyForm);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const selectedBinding = useMemo(() => bindings.find((b) => sameId(b.id, selectedBindingId)) || null, [bindings, selectedBindingId]);
  const canCreate = !!selectedTemplate?.id && selectedVersion?.status === "published";

  const tree = form.domain === "work_package" ? workPackageTaxonomyTypes : taxonomyTypes;
  const selectedType = findNode(tree, form.type_id);
  const subtypes = childrenFor(selectedType, "subtype");
  const selectedSubtype = findNode(subtypes, form.subtype_id);
  const subtype2s = childrenFor(selectedSubtype, "subtype2");

  const bindingPath = (binding) => {
    const meta = scopeMeta[binding.binding_scope];
    if (!meta) return binding.binding_scope;
    const [domain, field] = meta;
    const source = domain === "work_package" ? workPackageTaxonomyTypes : taxonomyTypes;
    const target = binding[field];
    for (const type of source) {
      if (sameId(type.id, target)) return type.name || `#${target}`;
      for (const sub of childrenFor(type, "subtype")) {
        if (sameId(sub.id, target)) return `${type.name} → ${sub.name}`;
        for (const sub2 of childrenFor(sub, "subtype2")) if (sameId(sub2.id, target)) return `${type.name} → ${sub.name} → ${sub2.name}`;
      }
    }
    return `#${target}`;
  };

  useEffect(() => {
    if (!selectedBinding) { setForm(emptyForm); setAdvancedOpen(false); return; }
    const [domain] = scopeMeta[selectedBinding.binding_scope] || ["job"];
    setForm({
      domain,
      type_id: selectedBinding.taxonomy_type_id ?? selectedBinding.work_package_type_id ?? "",
      subtype_id: selectedBinding.taxonomy_subtype_id ?? selectedBinding.work_package_subtype_id ?? "",
      subtype2_id: selectedBinding.taxonomy_subtype2_id ?? selectedBinding.work_package_subtype2_id ?? "",
      priority: selectedBinding.priority ?? 100,
      is_default: !!selectedBinding.is_default,
      is_active: !!selectedBinding.is_active,
      effective_from: toInputDate(selectedBinding.effective_from),
      effective_to: toInputDate(selectedBinding.effective_to),
    });
  }, [selectedBinding]);

  const newBinding = () => { onSelectBinding?.(null); setForm(emptyForm); setAdvancedOpen(false); };
  const changeDomain = (domain) => setForm((p) => ({ ...emptyForm, domain, priority: p.priority, is_default: p.is_default }));
  const changeType = (id) => setForm((p) => ({ ...p, type_id: id, subtype_id: "", subtype2_id: "" }));
  const changeSubtype = (id) => setForm((p) => ({ ...p, subtype_id: id, subtype2_id: "" }));

  const createPayload = () => {
    if (!form.type_id) throw new Error(`Select a ${form.domain === "work_package" ? "Work Package" : "Job"} Type.`);
    const level = form.subtype2_id ? "subtype2" : form.subtype_id ? "subtype" : "type";
    const prefix = form.domain === "work_package" ? "work_package" : "job";
    const binding_scope = `${prefix}_${level}`;
    const payload = {
      binding_scope,
      taxonomy_type_id: null, taxonomy_subtype_id: null, taxonomy_subtype2_id: null,
      work_package_type_id: null, work_package_subtype_id: null, work_package_subtype2_id: null,
      priority: Number(form.priority) || 0, is_default: !!form.is_default, is_active: true,
      effective_from: fromInputDate(form.effective_from), effective_to: fromInputDate(form.effective_to),
    };
    const field = scopeMeta[binding_scope][1];
    payload[field] = Number(level === "subtype2" ? form.subtype2_id : level === "subtype" ? form.subtype_id : form.type_id);
    return payload;
  };

  const save = async () => {
    try {
      if (selectedBinding) {
        await onUpdateBinding?.(selectedBinding.id, {
          priority: Number(form.priority) || 0,
          is_default: !!form.is_default,
          is_active: !!form.is_active,
          effective_from: fromInputDate(form.effective_from),
          effective_to: fromInputDate(form.effective_to),
        });
        return;
      }
      if (!canCreate) return alert("Publish the workflow version before creating its binding.");
      await onCreateBinding?.({ ...createPayload(), workflow_template_id: selectedTemplate.id, workflow_template_version_id: selectedVersion.id });
      setForm(emptyForm);
    } catch (err) { alert(err?.message || "Failed to save binding."); }
  };

  const textColor = "#1f2937";
  const mutedColor = "#64748b";
  const borderColor = "#e5e7eb";
  const actionBackground = theme.button_action?.normal || theme.accent_color || "#f97316";
  const input = { width: "100%", minHeight: 40, border: `1px solid ${borderColor}`, borderRadius: 8, padding: "8px 10px", boxSizing: "border-box", background: "#ffffff", color: textColor };
  const label = { fontSize: 12, fontWeight: 700, marginBottom: 4, color: textColor };
  const button = { minHeight: 40, border: `1px solid ${borderColor}`, borderRadius: 8, padding: "8px 12px", fontWeight: 700, cursor: "pointer", background: "#ffffff", color: textColor };
  const card = { border: `1px solid ${borderColor}`, borderRadius: 10, padding: 10, background: "#ffffff", color: textColor };

  return <div className="flex h-auto min-h-0 flex-col gap-3 md:h-full md:flex-row">
    <div className="flex min-h-0 flex-col gap-2 md:w-5/12">
      <div className="flex items-center justify-between"><div><div style={{fontWeight:700}}>Applies To</div><div style={{fontSize:12,color:mutedColor}}>Where this workflow is selected automatically.</div></div><button type="button" style={{...button,background:theme.button_action?.normal||"#111",color:"#fff"}} onClick={newBinding}>+ New</button></div>
      <div className="flex min-h-0 flex-col gap-2 overflow-y-auto">
        {(bindings || []).map((b) => <div key={b.id} style={{...card,cursor:"pointer",opacity:b.is_active?1:.65,borderColor:sameId(b.id,selectedBindingId)?actionBackground:borderColor}} onClick={()=>onSelectBinding?.(b.id)}>
          <div style={{fontWeight:700}}>{scopeMeta[b.binding_scope]?.[0] === "work_package" ? "Work Package" : "Job"} · {bindingPath(b)}</div>
          <div style={{fontSize:12,color:mutedColor}}>{b.is_active ? "Active" : "Inactive"}{b.is_default ? " · Default" : ""} · Priority {b.priority}</div>
        </div>)}
        {!loading.bindings && (!bindings || bindings.length===0) && <div style={{...card,color:mutedColor}}>No bindings yet.</div>}
      </div>
    </div>

    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto" style={card}>
      <div>
        <div style={{fontWeight:700}}>{selectedBinding ? "Binding Details" : `Create Binding${selectedTemplate?.name ? ` — ${selectedTemplate.name}` : ""}`}</div>
        <div style={{fontSize:12,color:mutedColor}}>{selectedBinding ? "The target is locked for audit history. Deactivate and create a new binding to change it." : "Choose Job or Work Package, then select the most specific applicable taxonomy."}</div>
      </div>
      {selectedTemplate?.id && <div style={{...card, background:"#f8fafc"}}>
        <div style={{fontSize:11,fontWeight:700,color:mutedColor,textTransform:"uppercase",letterSpacing:"0.04em"}}>Selected Workflow</div>
        <div style={{fontWeight:700,marginTop:2}}>{selectedTemplate.name || selectedTemplate.template_key || `Workflow #${selectedTemplate.id}`}</div>
        <div style={{fontSize:12,color:mutedColor,marginTop:2}}>
          {selectedVersion ? `Version v${selectedVersion.version_no ?? "?"} · ${selectedVersion.status || "unknown"}` : "No version selected"}
        </div>
        {selectedVersion?.status !== "published" && <div style={{fontSize:12,color:"#9a3412",marginTop:6}}>
          You can choose the binding target now. Publish this workflow version before creating the binding.
        </div>}
      </div>}
      <div><div style={label}>Applies to</div><select style={input} value={form.domain} disabled={!!selectedBinding} onChange={(e)=>changeDomain(e.target.value)}><option value="job">Job</option><option value="work_package">Work Package</option></select></div>
      <div><div style={label}>{form.domain === "work_package" ? "Work Package Type" : "Job Type"}</div><select style={input} value={form.type_id} disabled={!!selectedBinding || loading.taxonomy || loading.workPackageTaxonomy} onChange={(e)=>changeType(e.target.value)}><option value="">Select type…</option>{(tree||[]).map((n)=><option key={n.id} value={n.id}>{n.name}</option>)}</select></div>
      <div><div style={label}>Subtype <span style={{fontWeight:400}}>(optional)</span></div><select style={input} value={form.subtype_id} disabled={!!selectedBinding || !form.type_id} onChange={(e)=>changeSubtype(e.target.value)}><option value="">Use whole type</option>{subtypes.map((n)=><option key={n.id} value={n.id}>{n.name}</option>)}</select></div>
      <div><div style={label}>Subtype 2 <span style={{fontWeight:400}}>(optional)</span></div><select style={input} value={form.subtype2_id} disabled={!!selectedBinding || !form.subtype_id} onChange={(e)=>setForm((p)=>({...p,subtype2_id:e.target.value}))}><option value="">Use selected subtype</option>{subtype2s.map((n)=><option key={n.id} value={n.id}>{n.name}</option>)}</select></div>

      <button type="button" style={button} onClick={()=>setAdvancedOpen((v)=>!v)}>{advancedOpen ? "Hide advanced options" : "Advanced options"}</button>
      {advancedOpen && <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div><div style={label}>Priority</div><input style={input} type="number" min="0" value={form.priority} onChange={(e)=>setForm((p)=>({...p,priority:e.target.value}))}/></div>
        <label className="flex items-center gap-2 self-end" style={{minHeight:40}}><input type="checkbox" checked={form.is_default} onChange={(e)=>setForm((p)=>({...p,is_default:e.target.checked}))}/> Default binding</label>
        <div><div style={label}>Effective from</div><input style={input} type="datetime-local" value={form.effective_from} onChange={(e)=>setForm((p)=>({...p,effective_from:e.target.value}))}/></div>
        <div><div style={label}>Effective to</div><input style={input} type="datetime-local" value={form.effective_to} onChange={(e)=>setForm((p)=>({...p,effective_to:e.target.value}))}/></div>
      </div>}
      <div className="flex flex-wrap gap-2">
        <button type="button" style={{...button,background:theme.button_action?.normal||"#111",color:"#fff"}} disabled={loading.savingBinding || (!selectedBinding && !canCreate)} onClick={save}>{loading.savingBinding ? "Saving…" : selectedBinding ? "Save Binding Settings" : canCreate ? "Create Binding" : "Publish Before Binding"}</button>
        {selectedBinding?.is_active && <button type="button" style={{...button,background:"#fff",color:"#b91c1c",borderColor:"#fecaca"}} disabled={loading.deactivatingBinding} onClick={()=>onDeactivateBinding?.(selectedBinding.id,{is_active:false})}>Deactivate</button>}
      </div>
    </div>
  </div>;
};

export default WorkflowBindingAdminWidget;
